class ArticleToc extends HTMLElement {
  connectedCallback() {
    this._entries = [];
    this._activeIndex = -1;
    this._locked = false;
    this._unlockTimer = null;
    this._raf = 0;
    this._mobileDir = null;
    this._cachedHeaderH = null;

    if (document.readyState === "loading") {
      // DOM 尚未解析完：等 DOMContentLoaded 后立即初始化，不加 rAF
      document.addEventListener("DOMContentLoaded", () => this._init(), { once: true });
    } else {
      // DOM 已就绪（interactive / complete）：直接同步初始化，避免额外帧延迟
      this._init();
    }
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this._onScroll, { passive: true });
    window.removeEventListener("resize", this._onResize, { passive: true });
    window.removeEventListener("load", this._onLoad);
    if (this._unlockTimer) clearTimeout(this._unlockTimer);
    if (this._raf) cancelAnimationFrame(this._raf);
  }

  _init() {
    const tocList = this.querySelector(".directory-items");
    if (!tocList) return;

    // 静态第一项：不依赖 #introduction，点击一律回顶
    const staticItem = tocList.querySelector(".article-directory-item");
    if (staticItem) {
      const a = staticItem.querySelector("a");
      if (a) a.setAttribute("href", "#");
      this._entries.push({ item: staticItem, targets: [], toTop: true });
    }

    const content = this.querySelector(".article-content-inner");
    if (content) this._buildHeadings(content, tocList);

    if (!this._entries.length) return;

    this._onScroll = () => this._queueScrollSync();
    this._onResize = () => {
      this._cachedHeaderH = null; // resize 时清除缓存，下次重新测量
      this._syncMobile();
    };
    this._onLoad = () => this._syncActiveFromScroll();

    this.addEventListener("click", (e) => this._onClick(e));
    window.addEventListener("scroll", this._onScroll, { passive: true });
    window.addEventListener("resize", this._onResize, { passive: true });

    this._syncMobile();
    this._syncActiveFromScroll();
    if (document.readyState === "complete") {
      requestAnimationFrame(() => this._syncActiveFromScroll());
    } else {
      window.addEventListener("load", this._onLoad, { once: true });
    }
  }

  _buildHeadings(content, tocList) {
    const cls = this.dataset.headingClass || "toc-heading";
    let headings = content.querySelectorAll("h2." + cls);
    if (!headings.length) headings = content.querySelectorAll("h2");

    const byText = new Map();
    const fragment = document.createDocumentFragment();

    headings.forEach((h2, i) => {
      const text = h2.textContent.trim();
      const id = "toc-h2-" + i;
      h2.id = id;

      if (byText.has(text)) {
        byText.get(text).targets.push(h2);
        return;
      }

      const item = document.createElement("div");
      item.className = "article-directory-item";
      const a = document.createElement("a");
      a.href = "#" + id;
      a.textContent = text;
      item.appendChild(a);
      fragment.appendChild(item); // 先收集到 fragment，避免逐个触发 reflow

      const entry = { item, targets: [h2], toTop: i === 0 };
      byText.set(text, entry);
      this._entries.push(entry);
    });

    tocList.appendChild(fragment); // 一次性插入 DOM，只触发一次排版
  }

  _onClick(e) {
    const item = e.target.closest(".article-directory-item");
    if (!item || !this.contains(item)) return;

    const list = item.parentElement;
    if (!list || !list.classList.contains("directory-items")) return;

    const index = [...list.children].indexOf(item);
    const entry = this._entries[index];
    if (!entry) return;

    e.preventDefault();
    this._setActive(index);

    if (entry.toTop) {
      this._scrollTo(0);
      return;
    }

    const el = entry.targets[0];
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.pageYOffset - this._scrollOffset();
    this._scrollTo(Math.max(0, top));
  }

  _scrollTo(y) {
    this._locked = true;
    window.scrollTo({ top: y, behavior: "smooth" });
    this._armUnlock();
  }

  _queueScrollSync() {
    if (this._raf) return;
    this._raf = requestAnimationFrame(() => {
      this._raf = 0;
      if (this._locked) {
        this._armUnlock();
        return;
      }
      this._syncActiveFromScroll();
    });
  }

  // 滚动停止约 160ms 后再解锁并校准高亮（避免打断 smooth）
  _armUnlock() {
    if (this._unlockTimer) clearTimeout(this._unlockTimer);
    this._unlockTimer = setTimeout(() => {
      this._unlockTimer = null;
      this._locked = false;
      this._syncActiveFromScroll();
    }, 160);
  }

  _syncActiveFromScroll() {
    if (!this._entries.length) return;

    const line = window.pageYOffset + this._activateOffset();

    // 取「已越过参考线」的最后一项；都未越过则停在第一项（回顶项 / 首个 H2）
    let active = 0;
    this._entries.forEach((entry, index) => {
      if (entry.toTop) return;
      entry.targets.forEach((el) => {
        const y = el.getBoundingClientRect().top + window.pageYOffset;
        if (y <= line) active = index;
      });
    });

    this._setActive(active);
  }

  _setActive(index) {
    if (index !== this._activeIndex) {
      this._activeIndex = index;
      this._entries.forEach((entry, i) => {
        entry.item.classList.toggle("active", i === index);
      });
    }

    if (!this._mobileDir) return;
    this._mobileDir.querySelectorAll(".article-directory-item").forEach((el, i) => {
      el.classList.toggle("active", i === index);
    });
  }

  _activateOffset() {
    // 激活检测线 = 视口顶部 30%，标题越过这里时切换 TOC 高亮
    return window.innerHeight * 0.3;
  }

  _scrollOffset() {
    // 点击 TOC 的滚动目标 = 导航栏底部 + 20px（移动端 10px）
    const gap = window.innerWidth < 768 ? 20 : 30;
    return this._headerHeight() + gap;
  }

  _headerHeight() {
    if (this._cachedHeaderH == null) {
      const header = document.querySelector(".header-container");
      this._cachedHeaderH = header ? header.getBoundingClientRect().height : 0;
    }
    return this._cachedHeaderH;
  }

  _syncMobile() {
    if (window.innerWidth < 768) {
      this._injectMobile();
    } else {
      this._removeMobile();
    }
  }

  _injectMobile() {
    if (this._mobileDir) return;
    const original = this.querySelector(".article-directory");
    if (!original) return;

    const clone = original.cloneNode(true);
    clone.classList.add("article-directory--mobile");
    this.appendChild(clone);
    this._mobileDir = clone;
    // 强制把当前高亮同步到克隆（_activeIndex 可能已是目标值）
    const index = this._activeIndex < 0 ? 0 : this._activeIndex;
    this._mobileDir.querySelectorAll(".article-directory-item").forEach((el, i) => {
      el.classList.toggle("active", i === index);
    });
  }

  _removeMobile() {
    if (!this._mobileDir) return;
    this._mobileDir.remove();
    this._mobileDir = null;
  }
}

if (!window.customElements.get("article-toc")) {
  window.customElements.define("article-toc", ArticleToc);
}
